import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { 
  getAllImageReportRequests,
  updateImageReportRequestStatus,
  getImageReportRequestStats,
  ImageReportRequest 
} from '../../services/imageReportService';
import commandService, { Command } from '../../services/commandService';
import { formatPersianDateTime, formatPersianDate } from '../../utils/persianDate';
import socketService from '../../services/socketService';
import API_CONFIG from '../../config/api';

// Translate status to Persian
const translateStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    pending: 'در حال بررسی',
    approved: 'تایید شده',
    rejected: 'تایید نشده'
  };
  return statusMap[status] || status;
};

// Get status color
const getStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800'
  };
  return colorMap[status] || 'bg-gray-100 text-gray-800';
};

interface RequestStats {
  totalRequests: number;
  pendingRequests: number;
  approvedRequests: number;
  rejectedRequests: number;
  requestsToday: number;
}

const ImageReportRequests: React.FC = () => {
  const [requests, setRequests] = useState<ImageReportRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<ImageReportRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<RequestStats>({
    totalRequests: 0,
    pendingRequests: 0,
    approvedRequests: 0,
    rejectedRequests: 0,
    requestsToday: 0
  });
  const [selectedRequest, setSelectedRequest] = useState<ImageReportRequest | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');
  
  // Rejection problems (reasons for image report rejections)
  const [problems, setProblems] = useState<Command[]>([]);
  const [selectedProblems, setSelectedProblems] = useState<string[]>([]);
  const [loadingProblems, setLoadingProblems] = useState(false);
  
  // Modal form state
  const [modalForm, setModalForm] = useState({
    reportPdf: null as File | null
  });

  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [requests, statusFilter, searchTerm]);

  // Setup socket listener for new image report requests
  useEffect(() => {
    const handleNewImageReportRequest = (notification: any) => {

      // Refresh requests and stats
      fetchRequests();
      fetchStats();
      
      // Show toast notification
      toast.success(`درخواست جدید تصویر جواب از ${notification.imageReportRequest.userInfo} دریافت شد`, {
        position: "top-right",
        autoClose: 5000,
      });
    };

    // Set up socket listener
    socketService.setOnNewImageReportRequest(handleNewImageReportRequest);

    // Cleanup
    return () => {
      socketService.setOnNewImageReportRequest(() => {});
    };
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await getAllImageReportRequests({ limit: 100 });
      if (response.success) {
        setRequests(response.data);
      }
    } catch (error) {

      toast.error('خطا در دریافت درخواست‌ها');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getImageReportRequestStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {

    }
  };

  const loadProblems = async () => {
    try {
      setLoadingProblems(true);
      const commands = await commandService.getCommands();
      setProblems(commands.problems || []);
    } catch (error) {

      toast.error('خطا در بارگیری دلایل تصویر جواب آزمایش');
    } finally {
      setLoadingProblems(false);
    }
  };

  const filterRequests = () => {
    let filtered = requests;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(request => request.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(request => 
        request.patientInfo.admissionNumber.toLowerCase().includes(search) ||
        request.patientInfo.firstName.toLowerCase().includes(search) ||
        request.patientInfo.lastName.toLowerCase().includes(search) ||
        request.patientInfo.nationalId.includes(search)
      );
    }

    setFilteredRequests(filtered);
  };

  const handleAction = async (request: ImageReportRequest, action: 'approve' | 'reject') => {
    setSelectedRequest(request);
    setActionType(action);
    setSelectedProblems([]);
    setModalForm({
      reportPdf: null
    });
    
    // Load rejection problems if rejecting
    if (action === 'reject') {
      await loadProblems();
    }
    
    setShowModal(true);
  };

  const handleModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedRequest) return;

    if (actionType === 'reject' && selectedProblems.length === 0) {
      toast.error('لطفا حداقل یک دلیل عدم تایید را انتخاب کنید');
      return;
    }

    if (actionType === 'approve' && !modalForm.reportPdf) {
      toast.error('آپلود فایل PDF الزامی است');
      return;
    }

    try {
      let rejectionReason = '';
      
      if (actionType === 'reject') {
        // Convert selected problem IDs to text
        const selectedTexts = selectedProblems
          .map(id => problems.find(problem => problem._id === id)?.text)
          .filter(Boolean);
        
        rejectionReason = selectedTexts.length > 0 ? `• ${selectedTexts.join('\n• ')}` : '';
      }

      const statusData = {
        status: actionType === 'approve' ? 'approved' as const : 'rejected' as const,
        rejectionReason,
        reportPdf: modalForm.reportPdf || undefined
      };

      const response = await updateImageReportRequestStatus(selectedRequest._id, statusData);
      
      if (response.success) {
        toast.success(actionType === 'approve' ? 'درخواست تایید شد و PDF آپلود گردید. پیام اطلاع‌رسانی برای کاربر ارسال شد.' : 'درخواست رد شد و دلیل عدم تایید برای کاربر ارسال شد.');
        setShowModal(false);
        setSelectedRequest(null);
        setSelectedProblems([]);
        fetchRequests();
        fetchStats();
      }
    } catch (error: any) {

      const errorMessage = error.response?.data?.message || 'خطا در به‌روزرسانی درخواست';
      toast.error(errorMessage);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('فقط فایل‌های PDF مجاز هستند');
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        toast.error('حجم فایل نباید بیشتر از 10 مگابایت باشد');
        return;
      }
      
      setModalForm(prev => ({ ...prev, reportPdf: file }));
    }
  };

  const viewReceiptImage = (imagePath: string) => {
    // Create full URL for the file
    const fileUrl = imagePath.startsWith('http') 
      ? imagePath 
      : `${API_CONFIG.uploadURL}/${imagePath}`;
    
    // Check if it's a PDF file
    const isPdf = imagePath.toLowerCase().includes('.pdf') || fileUrl.toLowerCase().includes('.pdf');
    
    if (isPdf) {
      // For PDF files, open in new tab for download/viewing
      window.open(fileUrl, '_blank');
    } else {
      // For image files, show in modal
      setSelectedImageUrl(fileUrl);
      setShowImageModal(true);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">مدیریت درخواست‌های دریافت تصویر جواب آزمایش</h1>
        
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-500 mt-0.5 ml-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="text-blue-800 text-sm">
              <p className="font-medium mb-1">راهنمای بررسی درخواست‌ها:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li><strong>تایید:</strong> آپلود PDF جواب آزمایش + تغییر وضعیت به "تایید شده" + ارسال پیام به کاربر</li>
                <li><strong>عدم تایید:</strong> وارد کردن دلیل + تغییر وضعیت به "تایید نشده" + ارسال پیام به کاربر</li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-700 mb-1">کل درخواست‌ها</h3>
            <p className="text-2xl font-bold text-blue-900">{stats.totalRequests}</p>
          </div>
          
          <div className="bg-yellow-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-yellow-700 mb-1">در حال بررسی</h3>
            <p className="text-2xl font-bold text-yellow-900">{stats.pendingRequests}</p>
          </div>
          
          <div className="bg-green-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-green-700 mb-1">تایید شده</h3>
            <p className="text-2xl font-bold text-green-900">{stats.approvedRequests}</p>
          </div>
          
          <div className="bg-red-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-red-700 mb-1">تایید نشده</h3>
            <p className="text-2xl font-bold text-red-900">{stats.rejectedRequests}</p>
          </div>
          
          <div className="bg-purple-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-purple-700 mb-1">امروز</h3>
            <p className="text-2xl font-bold text-purple-900">{stats.requestsToday}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="جستجو در شماره پذیرش، نام، کد ملی..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">همه وضعیت‌ها</option>
              <option value="pending">در حال بررسی</option>
              <option value="approved">تایید شده</option>
              <option value="rejected">تایید نشده</option>
            </select>
          </div>
          
          <button
            onClick={fetchRequests}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            بارگذاری مجدد
          </button>
        </div>

        {/* Requests Table */}
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm">هیچ درخواستی یافت نشد</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">درخواست کننده</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">شماره پذیرش</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">تاریخ و ساعت درخواست</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">تاریخ و ساعت پاسخ</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">جزئیات</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRequests.map((request) => (
                  <tr key={request._id} className="hover:bg-gray-50">
                    {/* درخواست کننده */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-medium">
                          {request.patientInfo.firstName} {request.patientInfo.lastName}
                        </div>
                        <div className="text-xs text-gray-500">
                          کد ملی: {request.patientInfo.nationalId}
                        </div>
                      </div>
                    </td>
                    
                    {/* شماره پذیرش */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {request.patientInfo.admissionNumber}
                    </td>
                    
                    {/* تاریخ و ساعت درخواست */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatPersianDateTime(request.createdAt)}
                    </td>
                    
                    {/* تاریخ و ساعت پاسخ */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {request.adminResponse?.responseDate 
                        ? formatPersianDateTime(request.adminResponse.responseDate)
                        : '---'
                      }
                    </td>
                    
                    {/* جزئیات */}
                    <td className="px-6 py-4 text-sm">
                      <div className="space-y-2">
                        {/* وضعیت */}
                        <div>
                          <span className={`inline-flex px-2 py-1 text-xs rounded-full font-medium ${getStatusColor(request.status)}`}>
                            {translateStatus(request.status)}
                          </span>
                        </div>
                        
                        {/* اطلاعات اضافی */}
                        <div className="text-xs text-gray-500">
                          <div>تاریخ آزمایش: {formatPersianDate(request.patientInfo.testDate)}</div>
                          {request.description && (
                            <div className="mt-1">توضیحات: {request.description}</div>
                          )}
                        </div>
                        
                        {/* دکمه مشاهده فایل */}
                        <div>
                          <button
                            onClick={() => viewReceiptImage(request.receiptImage)}
                            className="text-blue-600 hover:text-blue-800 text-xs font-medium underline"
                          >
                            مشاهده فایل
                          </button>
                        </div>
                        
                        {/* عملیات */}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {request.status === 'pending' ? (
                            <>
                              <button
                                onClick={() => handleAction(request, 'approve')}
                                className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-md hover:bg-green-200 transition-colors"
                              >
                                ✓ تایید
                              </button>
                              <button
                                onClick={() => handleAction(request, 'reject')}
                                className="inline-flex items-center px-3 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-md hover:bg-red-200 transition-colors"
                              >
                                ✗ عدم تایید
                              </button>
                            </>
                          ) : (
                            <div className="text-xs">
                              {request.adminResponse?.responseDate && (
                                <div className="text-gray-600 mb-2">
                                  پاسخ داده شده در: {formatPersianDateTime(request.adminResponse.responseDate)}
                                </div>
                              )}
                              {request.adminResponse?.rejectionReason && (
                                <div className="text-red-600 p-2 bg-red-50 rounded">
                                  <strong>دلیل عدم تایید:</strong> {request.adminResponse.rejectionReason}
                                </div>
                              )}
                              {request.status === 'approved' && (
                                <div className="text-green-600 font-medium">
                                  ✓ تایید شده - PDF آماده دانلود
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Action Modal */}
      {showModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              {actionType === 'approve' ? 'تایید درخواست و آپلود PDF جواب آزمایش' : 'عدم تایید درخواست'}
            </h3>
            
            <div className="mb-4 p-3 bg-gray-50 rounded-md">
              <p className="text-sm font-medium">درخواست:</p>
              <p className="text-sm text-gray-600">
                {selectedRequest.patientInfo.firstName} {selectedRequest.patientInfo.lastName} - 
                شماره پذیرش: {selectedRequest.patientInfo.admissionNumber}
              </p>
            </div>

            <form onSubmit={handleModalSubmit} className="space-y-4">
              {actionType === 'reject' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    انتخاب دلایل عدم تایید *
                  </label>
                  {loadingProblems ? (
                    <div className="text-center py-4">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-red-500"></div>
                      <p className="mt-2 text-sm text-gray-600">در حال بارگیری دلایل...</p>
                    </div>
                  ) : (
                    <Select
                      isMulti
                      options={problems.map(problem => ({
                        value: problem._id,
                        label: problem.text
                      }))}
                      value={problems
                        .filter(problem => selectedProblems.includes(problem._id))
                        .map(problem => ({
                          value: problem._id,
                          label: problem.text
                        }))}
                      onChange={(selectedOptions) => {
                        const selectedIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
                        setSelectedProblems(selectedIds);
                      }}
                      placeholder="دلایل عدم تایید را انتخاب کنید..."
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      controlShouldRenderValue={true}
                      isSearchable={true}
                      noOptionsMessage={() => "هیچ دلیلی یافت نشد"}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          borderRadius: '0.375rem',
                          borderColor: '#D1D5DB',
                          minHeight: '40px',
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: '#9CA3AF',
                        }),
                        multiValue: (provided) => ({
                          ...provided,
                          backgroundColor: '#FEE2E2',
                          borderRadius: '0.25rem',
                        }),
                        multiValueLabel: (provided) => ({
                          ...provided,
                          color: '#DC2626',
                          fontSize: '0.875rem',
                        }),
                        multiValueRemove: (provided) => ({
                          ...provided,
                          color: '#DC2626',
                          ':hover': {
                            backgroundColor: '#FCA5A5',
                            color: '#991B1B',
                          },
                        }),
                      }}
                    />
                  )}
                  
                  {selectedProblems.length > 0 && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                      <h4 className="text-sm font-medium text-red-800 mb-2">دلایل انتخاب شده:</h4>
                      <div className="text-sm text-red-700">
                        {selectedProblems.map((id) => {
                          const problem = problems.find(r => r._id === id);
                          return problem ? (
                            <div key={id} className="flex items-start mb-1">
                              <span className="ml-2">•</span>
                              <span>{problem.text}</span>
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                  
                  <div className="text-xs text-gray-500">
                    نکته: دلایل انتخاب شده برای کاربر نمایش داده خواهد شد.
                  </div>
                </div>
              )}

              {actionType === 'approve' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    آپلود فایل PDF جواب آزمایش *
                  </label>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    فقط فایل‌های PDF - حداکثر حجم: 10 مگابایت
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                >
                  انصراف
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 text-white rounded-md ${
                    actionType === 'approve' 
                      ? 'bg-green-500 hover:bg-green-600' 
                      : 'bg-red-500 hover:bg-red-600'
                  }`}
                >
                  {actionType === 'approve' ? 'تایید' : 'عدم تایید'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-4xl max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">تصویر قبض</h3>
              <button
                onClick={() => setShowImageModal(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="بستن تصویر"
                title="بستن تصویر"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <img 
              src={selectedImageUrl} 
              alt="Receipt" 
              className="max-w-full max-h-[70vh] object-contain mx-auto"
              onError={(e) => {

                e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y5ZmFmYiIvPjx0ZXh0IHg9IjE1MCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM2YjczODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiPlxuRmlsZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageReportRequests;